import React, { useState } from 'react';
import { Send, AlertCircle, CheckCircle, ArrowLeft, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// Ensure BACKEND_URL is an absolute URL with protocol
const getBackendUrl = () => {
  const url = process.env.REACT_APP_BACKEND_URL;
  if (!url) {
    console.error('REACT_APP_BACKEND_URL is not set');
    return '';
  }
  // If URL doesn't start with http:// or https://, add https://
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
};

const BACKEND_URL = getBackendUrl();
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '';

export const ShopifyQuestionnaire = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Contact and project info
    contactName: '',
    email: '',
    projectName: '',
    existingWebsiteUrl: '',
    
    // Shopify access
    storeCreated: '',
    newStoreName: '',
    storeOwnerEmail: '',
    shopifyStoreUrl: '',
    collaboratorAccess: '',
    
    // Development scope
    startWorkOn: '',
    existingTheme: '',
    designInspirationUrl: '',
    
    // Custom development
    customLiquidJs: '',
    thirdPartyIntegration: '',
    customFunctionalityDesc: '',
    
    // Timeline
    desiredStartDate: '',
    targetLaunchDate: '',
    
    // Notes
    technicalNotes: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.contactName.trim()) newErrors.contactName = "Required";
      if (!formData.email.trim()) {
        newErrors.email = "Required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Invalid email";
      }
      if (!formData.projectName.trim()) newErrors.projectName = "Required";
    }
    
    if (step === 2) {
      if (!formData.storeCreated) newErrors.storeCreated = "Required";
      
      if (formData.storeCreated === 'no') {
        if (!formData.newStoreName.trim()) newErrors.newStoreName = "Required";
        if (!formData.storeOwnerEmail.trim()) {
          newErrors.storeOwnerEmail = "Required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.storeOwnerEmail)) {
          newErrors.storeOwnerEmail = "Invalid email";
        }
      } else if (formData.storeCreated === 'yes') {
        if (!formData.shopifyStoreUrl.trim()) newErrors.shopifyStoreUrl = "Required";
        if (!formData.collaboratorAccess) newErrors.collaboratorAccess = "Required";
      }
    }
    
    if (step === 3) {
      if (!formData.startWorkOn) newErrors.startWorkOn = "Required";
      if (!formData.existingTheme) newErrors.existingTheme = "Required";
      
      if (formData.existingTheme === 'no' && !formData.designInspirationUrl.trim()) {
        newErrors.designInspirationUrl = "Required";
      }
    }
    
    // Step 4 has no required fields (can be skipped)
    
    if (step === 5) {
      if (!formData.desiredStartDate) newErrors.desiredStartDate = "Required";
    }
    
    // Step 6 (Notes) has no required fields
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const hasRequiredFields = (step) => {
    return step !== 4 && step !== 6; // Steps 4 and 6 can be skipped
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      setErrors({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      toast.error("Please fill in all required fields");
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    setErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSkip = () => {
    if (!hasRequiredFields(currentStep)) {
      setCurrentStep(prev => prev + 1);
      setErrors({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepClick = (step) => {
    if (step < currentStep) {
      setCurrentStep(step);
      setErrors({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (step === currentStep + 1 && validateStep(currentStep)) {
      setCurrentStep(step);
      setErrors({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(5) && !validateStep(6)) return;
    
    setIsSubmitting(true);
    
    try {
      // Validate API URL is configured
      if (!API) {
        toast.error("Configuration Error", {
          description: "Backend URL is not configured. Please contact support."
        });
        setIsSubmitting(false);
        return;
      }

      const response = await axios.post(`${API}/shopify-questionnaire`, {
        ...formData,
        existingWebsiteUrl: formData.existingWebsiteUrl || null,
        newStoreName: formData.newStoreName || null,
        storeOwnerEmail: formData.storeOwnerEmail || null,
        shopifyStoreUrl: formData.shopifyStoreUrl || null,
        collaboratorAccess: formData.collaboratorAccess || null,
        designInspirationUrl: formData.designInspirationUrl || null,
        customLiquidJs: formData.customLiquidJs || null,
        thirdPartyIntegration: formData.thirdPartyIntegration || null,
        customFunctionalityDesc: formData.customFunctionalityDesc || null,
        targetLaunchDate: formData.targetLaunchDate || null,
        technicalNotes: formData.technicalNotes || null
      });
      
      if (response.data) {
        setIsSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      toast.error("Submission failed", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 flex items-center justify-center px-6 py-20">
        <div className="max-w-2xl w-full text-center">
          <div className="glass rounded-3xl p-12">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">Thank You!</h2>
            <p className="text-xl text-slate-300 mb-8">
              Your Shopify development questionnaire has been submitted successfully.
            </p>
            <p className="text-slate-400 mb-8">
              We'll review your requirements and reach out at <strong className="text-cyan-400">{formData.email}</strong> within 24-48 hours.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold px-8 py-6 text-lg rounded-xl"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const renderStep1 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white border-b border-slate-700 pb-3">Contact & Project Info</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="contactName">Contact Name *</Label>
          <Input 
            id="contactName" 
            value={formData.contactName} 
            onChange={(e) => handleInputChange('contactName', e.target.value)} 
            className={`bg-slate-900/50 border-slate-700 text-white h-12 mt-2 ${errors.contactName ? 'border-red-500' : ''}`} 
          />
          {errors.contactName && <p className="text-red-400 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.contactName}</p>}
        </div>
        
        <div>
          <Label htmlFor="email">Email Address *</Label>
          <Input 
            id="email" 
            type="email" 
            value={formData.email} 
            onChange={(e) => handleInputChange('email', e.target.value)} 
            className={`bg-slate-900/50 border-slate-700 text-white h-12 mt-2 ${errors.email ? 'border-red-500' : ''}`} 
          />
          {errors.email && <p className="text-red-400 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.email}</p>}
        </div>
      </div>
      
      <div>
        <Label htmlFor="projectName">Project or Store Name *</Label>
        <Input 
          id="projectName" 
          value={formData.projectName} 
          onChange={(e) => handleInputChange('projectName', e.target.value)} 
          placeholder="e.g., MyAwesomeStore" 
          className={`bg-slate-900/50 border-slate-700 text-white h-12 mt-2 ${errors.projectName ? 'border-red-500' : ''}`} 
        />
        {errors.projectName && <p className="text-red-400 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.projectName}</p>}
      </div>
      
      <div>
        <Label htmlFor="existingWebsiteUrl">Existing Website URL (optional)</Label>
        <Input 
          id="existingWebsiteUrl" 
          value={formData.existingWebsiteUrl} 
          onChange={(e) => handleInputChange('existingWebsiteUrl', e.target.value)} 
          placeholder="https://yoursite.com" 
          className="bg-slate-900/50 border-slate-700 text-white h-12 mt-2" 
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white border-b border-slate-700 pb-3">Shopify Access & Status</h2>
      
      <div>
        <Label>Is the Shopify store already created? *</Label>
        <RadioGroup value={formData.storeCreated} onValueChange={(val) => handleInputChange('storeCreated', val)} className="mt-2">
          <div className="flex items-center space-x-2 bg-slate-900/30 p-3 rounded-lg">
            <RadioGroupItem value="yes" id="store-yes" />
            <Label htmlFor="store-yes" className="font-normal cursor-pointer">Yes</Label>
          </div>
          <div className="flex items-center space-x-2 bg-slate-900/30 p-3 rounded-lg">
            <RadioGroupItem value="no" id="store-no" />
            <Label htmlFor="store-no" className="font-normal cursor-pointer">No</Label>
          </div>
        </RadioGroup>
        {errors.storeCreated && <p className="text-red-400 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.storeCreated}</p>}
      </div>
      
      {formData.storeCreated === 'no' && (
        <>
          <div>
            <Label htmlFor="newStoreName">Store Name *</Label>
            <Input 
              id="newStoreName" 
              value={formData.newStoreName} 
              onChange={(e) => handleInputChange('newStoreName', e.target.value)} 
              placeholder="Enter store name" 
              className={`bg-slate-900/50 border-slate-700 text-white h-12 mt-2 ${errors.newStoreName ? 'border-red-500' : ''}`} 
            />
            {errors.newStoreName && <p className="text-red-400 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.newStoreName}</p>}
          </div>
          
          <div>
            <Label htmlFor="storeOwnerEmail">Store Owner Email Address *</Label>
            <Input 
              id="storeOwnerEmail" 
              type="email" 
              value={formData.storeOwnerEmail} 
              onChange={(e) => handleInputChange('storeOwnerEmail', e.target.value)} 
              placeholder="owner@email.com" 
              className={`bg-slate-900/50 border-slate-700 text-white h-12 mt-2 ${errors.storeOwnerEmail ? 'border-red-500' : ''}`} 
            />
            {errors.storeOwnerEmail && <p className="text-red-400 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.storeOwnerEmail}</p>}
          </div>
        </>
      )}
      
      {formData.storeCreated === 'yes' && (
        <>
          <div>
            <Label htmlFor="shopifyStoreUrl">Shopify Store URL or myshopify.com URL *</Label>
            <Input 
              id="shopifyStoreUrl" 
              value={formData.shopifyStoreUrl} 
              onChange={(e) => handleInputChange('shopifyStoreUrl', e.target.value)} 
              placeholder="yourstore.myshopify.com" 
              className={`bg-slate-900/50 border-slate-700 text-white h-12 mt-2 ${errors.shopifyStoreUrl ? 'border-red-500' : ''}`} 
            />
            {errors.shopifyStoreUrl && <p className="text-red-400 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.shopifyStoreUrl}</p>}
          </div>
          
          <div>
            <Label>Can you grant Shopify collaborator access immediately? *</Label>
            <RadioGroup value={formData.collaboratorAccess} onValueChange={(val) => handleInputChange('collaboratorAccess', val)} className="mt-2">
              <div className="flex items-center space-x-2 bg-slate-900/30 p-3 rounded-lg">
                <RadioGroupItem value="yes" id="collab-yes" />
                <Label htmlFor="collab-yes" className="font-normal cursor-pointer">Yes</Label>
              </div>
              <div className="flex items-center space-x-2 bg-slate-900/30 p-3 rounded-lg">
                <RadioGroupItem value="no" id="collab-no" />
                <Label htmlFor="collab-no" className="font-normal cursor-pointer">No</Label>
              </div>
            </RadioGroup>
            {errors.collaboratorAccess && <p className="text-red-400 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.collaboratorAccess}</p>}
          </div>
        </>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white border-b border-slate-700 pb-3">Initial Development Scope</h2>
      
      <div>
        <Label>What should I start working on first? *</Label>
        <Select value={formData.startWorkOn} onValueChange={(val) => handleInputChange('startWorkOn', val)}>
          <SelectTrigger className={`bg-slate-900/50 border-slate-700 text-white h-12 mt-2 ${errors.startWorkOn ? 'border-red-500' : ''}`}>
            <SelectValue placeholder="Select initial work" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="theme-setup">Theme setup</SelectItem>
            <SelectItem value="theme-customization">Theme customization</SelectItem>
            <SelectItem value="custom-sections">Custom sections or blocks</SelectItem>
            <SelectItem value="bug-fixes">Bug fixes</SelectItem>
            <SelectItem value="full-build">Full store build</SelectItem>
          </SelectContent>
        </Select>
        {errors.startWorkOn && <p className="text-red-400 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.startWorkOn}</p>}
      </div>
      
      <div>
        <Label>Is there an existing theme installed? *</Label>
        <RadioGroup value={formData.existingTheme} onValueChange={(val) => handleInputChange('existingTheme', val)} className="mt-2">
          <div className="flex items-center space-x-2 bg-slate-900/30 p-3 rounded-lg">
            <RadioGroupItem value="yes" id="theme-yes" />
            <Label htmlFor="theme-yes" className="font-normal cursor-pointer">Yes</Label>
          </div>
          <div className="flex items-center space-x-2 bg-slate-900/30 p-3 rounded-lg">
            <RadioGroupItem value="no" id="theme-no" />
            <Label htmlFor="theme-no" className="font-normal cursor-pointer">No</Label>
          </div>
        </RadioGroup>
        {errors.existingTheme && <p className="text-red-400 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.existingTheme}</p>}
      </div>
      
      {formData.existingTheme === 'no' && (
        <div>
          <Label htmlFor="designInspirationUrl">URL of design or website to use as inspiration *</Label>
          <Input 
            id="designInspirationUrl" 
            value={formData.designInspirationUrl} 
            onChange={(e) => handleInputChange('designInspirationUrl', e.target.value)} 
            placeholder="https://inspiration-site.com" 
            className={`bg-slate-900/50 border-slate-700 text-white h-12 mt-2 ${errors.designInspirationUrl ? 'border-red-500' : ''}`} 
          />
          {errors.designInspirationUrl && <p className="text-red-400 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.designInspirationUrl}</p>}
        </div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white border-b border-slate-700 pb-3">Custom Development Requirements</h2>
      <p className="text-slate-400 text-sm">You can skip this step if you're unsure about technical requirements.</p>
      
      <div>
        <Label>Do you need custom Liquid or JavaScript work?</Label>
        <Select value={formData.customLiquidJs} onValueChange={(val) => handleInputChange('customLiquidJs', val)}>
          <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white h-12 mt-2">
            <SelectValue placeholder="Select option" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
            <SelectItem value="dont-know">I don't know</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>Do you need third-party API or service integration?</Label>
        <Select value={formData.thirdPartyIntegration} onValueChange={(val) => handleInputChange('thirdPartyIntegration', val)}>
          <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white h-12 mt-2">
            <SelectValue placeholder="Select option" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
            <SelectItem value="dont-know">I don't know</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label htmlFor="customFunctionalityDesc">Brief description of required custom functionality</Label>
        <Textarea 
          id="customFunctionalityDesc" 
          value={formData.customFunctionalityDesc} 
          onChange={(e) => handleInputChange('customFunctionalityDesc', e.target.value)} 
          placeholder="Describe any custom features or functionality needed..." 
          className="bg-slate-900/50 border-slate-700 text-white min-h-[120px] mt-2" 
        />
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white border-b border-slate-700 pb-3">Timeline</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="desiredStartDate">Desired Start Date *</Label>
          <Input 
            id="desiredStartDate" 
            type="date" 
            value={formData.desiredStartDate} 
            onChange={(e) => handleInputChange('desiredStartDate', e.target.value)} 
            className={`bg-slate-900/50 border-slate-700 text-white h-12 mt-2 ${errors.desiredStartDate ? 'border-red-500' : ''}`} 
          />
          {errors.desiredStartDate && <p className="text-red-400 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.desiredStartDate}</p>}
        </div>
        
        <div>
          <Label htmlFor="targetLaunchDate">Target Launch Date (optional)</Label>
          <Input 
            id="targetLaunchDate" 
            type="date" 
            value={formData.targetLaunchDate} 
            onChange={(e) => handleInputChange('targetLaunchDate', e.target.value)} 
            className="bg-slate-900/50 border-slate-700 text-white h-12 mt-2" 
          />
        </div>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white border-b border-slate-700 pb-3">Additional Notes</h2>
      
      <div>
        <Label htmlFor="technicalNotes">Technical constraints or instructions before development starts (optional)</Label>
        <Textarea 
          id="technicalNotes" 
          value={formData.technicalNotes} 
          onChange={(e) => handleInputChange('technicalNotes', e.target.value)} 
          placeholder="Any specific requirements, constraints, or instructions we should know about..." 
          className="bg-slate-900/50 border-slate-700 text-white min-h-[150px] mt-2" 
        />
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      default: return null;
    }
  };

  const steps = [
    { number: 1, title: 'Contact & Project' },
    { number: 2, title: 'Shopify Access' },
    { number: 3, title: 'Development Scope' },
    { number: 4, title: 'Custom Development' },
    { number: 5, title: 'Timeline' },
    { number: 6, title: 'Notes' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 px-6 py-20">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-cyan-400 hover:text-cyan-300 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Shopify Development <span className="gradient-text">Questionnaire</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Quick setup to get started with your Shopify development project
          </p>
        </div>

        {/* Form */}
        <div className="glass rounded-3xl p-8 md:p-12">
          {/* Progress Indicator */}
          <div className="mb-10">
            <div className="flex items-center justify-center mb-4">
              {steps.map((step, idx) => (
                <div key={step.number} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => handleStepClick(step.number)}
                    disabled={step.number > currentStep + 1}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      step.number < currentStep
                        ? 'bg-cyan-500 text-white hover:bg-cyan-600 cursor-pointer'
                        : step.number === currentStep
                        ? 'bg-cyan-500 text-white ring-4 ring-cyan-500/30 cursor-default'
                        : step.number === currentStep + 1
                        ? 'bg-slate-700 text-slate-400 hover:bg-slate-600 cursor-pointer'
                        : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                    } ${step.number <= currentStep || step.number === currentStep + 1 ? '' : 'opacity-50'}`}
                  >
                    {step.number < currentStep ? <Check className="w-5 h-5" /> : step.number}
                  </button>
                  {idx < steps.length - 1 && (
                    <div
                      className={`w-12 h-1 mx-2 ${
                        step.number < currentStep ? 'bg-cyan-500' : 'bg-slate-700'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="text-center">
              <p className="text-slate-400 text-sm">
                Step {currentStep} of {steps.length}
              </p>
              <p className="text-white font-semibold mt-1">
                {steps[currentStep - 1].title}
              </p>
            </div>
          </div>

          {/* Form Content */}
          <div className="mb-8">
            {renderStepContent()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex gap-4">
            {currentStep > 1 && (
              <Button
                onClick={handleBack}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800 h-12"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            )}
            
            {!hasRequiredFields(currentStep) && currentStep < 6 && (
              <Button
                onClick={handleSkip}
                variant="ghost"
                className="text-slate-400 hover:text-slate-300 hover:bg-slate-800"
              >
                Skip
              </Button>
            )}
            
            {currentStep < 6 ? (
              <Button
                onClick={handleNext}
                className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold h-12"
              >
                Next
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-semibold h-12"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Questionnaire'}
                <Send className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};